package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraOrder
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraReason
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraUnit
import com.composables.icons.lucide.EllipsisVertical
import com.composables.icons.lucide.Eye
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Pencil
import com.composables.icons.lucide.Plus
import com.composables.icons.lucide.Search
import com.composables.icons.lucide.Settings
import com.composables.icons.lucide.Trash2
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_action_delete
import portalvv.shared.generated.resources.compras_action_edit
import portalvv.shared.generated.resources.compras_action_view
import portalvv.shared.generated.resources.compras_actions_menu
import portalvv.shared.generated.resources.compras_col_actions
import portalvv.shared.generated.resources.compras_extra_add_order
import portalvv.shared.generated.resources.compras_extra_col_delivery_date
import portalvv.shared.generated.resources.compras_extra_col_description
import portalvv.shared.generated.resources.compras_extra_col_order_date
import portalvv.shared.generated.resources.compras_extra_col_reason
import portalvv.shared.generated.resources.compras_extra_col_unit
import portalvv.shared.generated.resources.compras_extra_manage_reasons
import portalvv.shared.generated.resources.compras_extra_no_results
import portalvv.shared.generated.resources.compras_extra_search_placeholder
import portalvv.shared.generated.resources.compras_orders
import portalvv.shared.generated.resources.compras_table_empty
import portalvv.shared.generated.resources.compras_total

private const val EXTRA_WEIGHT_DATE = 1f
private const val EXTRA_WEIGHT_UNIT = 1f
private const val EXTRA_WEIGHT_DESCRIPTION = 1f
private const val EXTRA_WEIGHT_REASON = 1.2f
private val EXTRA_ACTIONS_WIDTH = 44.dp
private val EXTRA_TABLE_MIN_WIDTH = 1100.dp

@Composable
fun PurchasesExtrasContent(
    period: MonthPeriod,
    orders: List<PurchasesExtraOrder>,
    loading: Boolean,
    canEdit: Boolean,
    canAdd: Boolean,
    units: List<PurchasesExtraUnit>,
    reasons: List<PurchasesExtraReason>,
    canManageReasons: Boolean,
    canCreateReason: Boolean,
    canEditReason: Boolean,
    reasonsLoading: Boolean,
    reasonError: String?,
    onSaveOrder: (PurchasesExtraOrder) -> Unit,
    onCreateOrder: (PurchasesExtraOrder) -> Unit,
    onDeleteOrder: (PurchasesExtraOrder) -> Unit,
    onCreateReason: (String) -> Unit,
    onRenameReason: (String, String) -> Unit,
    onChangeReasonStatus: (String, Boolean) -> Unit,
    onDismissReasonError: () -> Unit,
) {
    var query by remember(period) { mutableStateOf("") }
    var viewing by remember { mutableStateOf<PurchasesExtraOrder?>(null) }
    var editing by remember { mutableStateOf<PurchasesExtraOrder?>(null) }
    var deleting by remember { mutableStateOf<PurchasesExtraOrder?>(null) }
    var adding by remember { mutableStateOf(false) }
    var managingReasons by remember { mutableStateOf(false) }
    val filtered =
        remember(orders, query) {
            val term = query.trim()
            if (term.isEmpty()) {
                orders
            } else {
                orders.filter { order ->
                    order.description.contains(term, ignoreCase = true) ||
                        order.unitName?.contains(term, ignoreCase = true) == true ||
                        order.sourceUnitLabel?.contains(term, ignoreCase = true) == true
                }
            }
        }

    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
        Text(
            text =
                stringResource(
                    Res.string.compras_total,
                    stringResource(Res.string.compras_orders, orders.size),
                ),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            AppTextField(
                value = query,
                onValueChange = { query = it },
                placeholder = stringResource(Res.string.compras_extra_search_placeholder),
                leadingIcon = { Icon(Lucide.Search, contentDescription = null) },
                modifier = Modifier.weight(1f),
            )
            if (canManageReasons) {
                AppButton(
                    text = stringResource(Res.string.compras_extra_manage_reasons),
                    onClick = { managingReasons = true },
                    variant = PortalButtonVariant.Secondary,
                    leadingIcon = Lucide.Settings,
                )
            }
            if (canAdd) {
                AppButton(
                    text = stringResource(Res.string.compras_extra_add_order),
                    onClick = { adding = true },
                    leadingIcon = Lucide.Plus,
                )
            }
        }

        if (loading) {
            Box(
                modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space32),
                contentAlignment = Alignment.Center,
            ) {
                CircularProgressIndicator()
            }
        } else if (orders.isEmpty()) {
            Box(modifier = Modifier.fillMaxWidth().height(360.dp)) {
                EmptyState(message = stringResource(Res.string.compras_table_empty))
            }
        } else {
            PurchasesExtraOrdersTable(
                rows = filtered,
                emptyMessage = stringResource(Res.string.compras_extra_no_results),
                canEdit = canEdit,
                onView = { viewing = it },
                onEdit = { editing = it },
                onDelete = { deleting = it },
            )
        }
    }

    viewing?.let { order ->
        PurchasesExtraOrderDetailsDialog(
            order = order,
            onDismiss = { viewing = null },
        )
    }

    if (canEdit) {
        editing?.let { order ->
            PurchasesExtraOrderEditDialog(
                order = order,
                units = units,
                reasons = reasons,
                onDismiss = { editing = null },
                onSave = { updated ->
                    onSaveOrder(updated)
                    editing = null
                },
            )
        }
    }

    if (canEdit) {
        deleting?.let { order ->
            PurchasesExtraDeleteDialog(
                order = order,
                onDismiss = { deleting = null },
                onConfirm = {
                    onDeleteOrder(order)
                    deleting = null
                },
            )
        }
    }

    if (canAdd && adding) {
        PurchasesExtraOrderEditDialog(
            order = emptyExtraOrder(),
            units = units,
            reasons = reasons,
            isNew = true,
            onDismiss = { adding = false },
            onSave = { created ->
                onCreateOrder(created)
                adding = false
            },
        )
    }

    if (canManageReasons && managingReasons) {
        PurchasesExtraReasonsDialog(
            reasons = reasons,
            loading = reasonsLoading,
            error = reasonError,
            canCreate = canCreateReason,
            canEdit = canEditReason,
            onCreate = onCreateReason,
            onRename = onRenameReason,
            onChangeStatus = onChangeReasonStatus,
            onDismissError = onDismissReasonError,
            onDismiss = { managingReasons = false },
        )
    }
}

@Composable
private fun PurchasesExtraOrdersTable(
    rows: List<PurchasesExtraOrder>,
    emptyMessage: String,
    canEdit: Boolean,
    onView: (PurchasesExtraOrder) -> Unit,
    onEdit: (PurchasesExtraOrder) -> Unit,
    onDelete: (PurchasesExtraOrder) -> Unit,
) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val tableWidth = if (maxWidth < EXTRA_TABLE_MIN_WIDTH) EXTRA_TABLE_MIN_WIDTH else maxWidth
        val scrollState = rememberScrollState()

        Box(modifier = Modifier.fillMaxWidth().horizontalScroll(scrollState)) {
            Surface(
                modifier = Modifier.width(tableWidth),
                shape = AppShapes.medium,
                color = MaterialTheme.colorScheme.surface,
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    ExtraTableHeader()
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    if (rows.isEmpty()) {
                        Box(
                            modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space24),
                            contentAlignment = Alignment.Center,
                        ) {
                            Text(
                                text = emptyMessage,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                    } else {
                        rows.forEachIndexed { index, order ->
                            ExtraOrderRow(
                                order = order,
                                canEdit = canEdit,
                                onView = { onView(order) },
                                onEdit = { onEdit(order) },
                                onDelete = { onDelete(order) },
                            )
                            if (index < rows.lastIndex) {
                                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ExtraTableHeader() {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .background(AppPalette.Neutral50)
                .padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        ExtraHeaderCell(stringResource(Res.string.compras_extra_col_order_date), EXTRA_WEIGHT_DATE)
        ExtraHeaderCell(stringResource(Res.string.compras_extra_col_delivery_date), EXTRA_WEIGHT_DATE)
        ExtraHeaderCell(stringResource(Res.string.compras_extra_col_unit), EXTRA_WEIGHT_UNIT, TextAlign.Start)
        ExtraHeaderCell(stringResource(Res.string.compras_extra_col_description), EXTRA_WEIGHT_DESCRIPTION, TextAlign.Start)
        ExtraHeaderCell(stringResource(Res.string.compras_extra_col_reason), EXTRA_WEIGHT_REASON, TextAlign.Start)
        Box(modifier = Modifier.width(EXTRA_ACTIONS_WIDTH), contentAlignment = Alignment.Center) {
            Text(
                text = stringResource(Res.string.compras_col_actions),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun RowScope.ExtraHeaderCell(
    text: String,
    weight: Float,
    align: TextAlign = TextAlign.Center,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        textAlign = align,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun ExtraOrderRow(
    order: PurchasesExtraOrder,
    canEdit: Boolean,
    onView: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        ExtraBodyCell(formatExtraDate(order.orderDate), EXTRA_WEIGHT_DATE, TextAlign.Center)
        ExtraBodyCell(formatExtraDate(order.deliveryDate), EXTRA_WEIGHT_DATE, TextAlign.Center)
        ExtraBodyCell(order.unitName ?: "—", EXTRA_WEIGHT_UNIT, TextAlign.Start)
        ExtraBodyCell(order.description.ifBlank { "—" }, EXTRA_WEIGHT_DESCRIPTION, TextAlign.Start)
        ExtraBodyCell(extraReasonLabel(order.reasonName), EXTRA_WEIGHT_REASON, TextAlign.Start)
        Box(modifier = Modifier.width(EXTRA_ACTIONS_WIDTH), contentAlignment = Alignment.Center) {
            ExtraRowActions(canEdit = canEdit, onView = onView, onEdit = onEdit, onDelete = onDelete)
        }
    }
}

@Composable
private fun RowScope.ExtraBodyCell(
    text: String,
    weight: Float,
    align: TextAlign,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurface,
        textAlign = align,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun ExtraRowActions(
    canEdit: Boolean,
    onView: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }
    Box {
        IconButton(onClick = { expanded = true }, modifier = Modifier.size(28.dp)) {
            Icon(
                imageVector = Lucide.EllipsisVertical,
                contentDescription = stringResource(Res.string.compras_actions_menu),
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            DropdownMenuItem(
                text = { Text(stringResource(Res.string.compras_action_view)) },
                leadingIcon = { Icon(Lucide.Eye, contentDescription = null) },
                onClick = {
                    expanded = false
                    onView()
                },
            )
            if (canEdit) {
                DropdownMenuItem(
                    text = { Text(stringResource(Res.string.compras_action_edit)) },
                    leadingIcon = { Icon(Lucide.Pencil, contentDescription = null) },
                    onClick = {
                        expanded = false
                        onEdit()
                    },
                )
                DropdownMenuItem(
                    text = {
                        Text(
                            text = stringResource(Res.string.compras_action_delete),
                            color = MaterialTheme.colorScheme.error,
                        )
                    },
                    leadingIcon = {
                        Icon(
                            imageVector = Lucide.Trash2,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.error,
                        )
                    },
                    onClick = {
                        expanded = false
                        onDelete()
                    },
                )
            }
        }
    }
}

private fun emptyExtraOrder() =
    PurchasesExtraOrder(
        id = "",
        orderDate = "",
        deliveryDate = null,
        quantity = null,
        description = "",
        sourceUnitLabel = null,
        unitId = null,
        unitName = null,
        sortOrder = 0,
    )

internal fun formatExtraDate(value: String?): String {
    if (value.isNullOrBlank()) return "—"
    return runCatching {
        val date = LocalDate.parse(value)
        "${date.day.toString().padStart(2, '0')}/${(date.month.ordinal + 1).toString().padStart(2, '0')}/${date.year}"
    }.getOrDefault(value)
}
